import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";

export function sharePayload(plan) {
  return {
    city: plan.city,
    date: plan.date,
    budget: plan.budget,
    vibe: plan.vibe,
    stops: plan.stops,
    event_found: plan.event_found,
    city_center: plan.city_center,
    candidates: plan.candidates,
  };
}

export async function copyShareLink(plan) {
  const { data } = await api.post("/share", sharePayload(plan));
  const url = `${window.location.origin}/share/${data.id}`;
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Share link copied to clipboard!", { description: url });
  } catch {
    toast.success("Share link ready", { description: url, duration: 10000 });
  }
  return url;
}

export function voterId() {
  let id = localStorage.getItem("nightout-voter");
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)).replace(/-/g, "");
    localStorage.setItem("nightout-voter", id);
  }
  return id;
}

export function persistPlan(plan) {
  sessionStorage.setItem("nightout-plan", JSON.stringify(plan));
}

// Resolves the plan for the itinerary page from: a pending build request, a shared id, or router/session state.
export function usePlanSource(shared) {
  const location = useLocation();
  const navigate = useNavigate();
  const { shareId } = useParams();
  const pending = !shared ? location.state?.pending : null;

  const initialPlan = useMemo(() => {
    if (shared || pending) return null;
    if (location.state?.plan) return location.state.plan;
    const stored = sessionStorage.getItem("nightout-plan");
    return stored ? JSON.parse(stored) : null;
  }, [location.state, shared, pending]);

  const [plan, setPlan] = useState(initialPlan);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!shared && !pending && !plan) navigate("/");
  }, [shared, pending, plan, navigate]);

  useEffect(() => {
    if (!pending) return;
    let cancelled = false;
    api
      .post("/plan", pending)
      .then(({ data }) => {
        if (cancelled) return;
        persistPlan(data);
        setPlan(data);
        navigate("/itinerary", { replace: true, state: { plan: data } });
      })
      .catch((e) => {
        if (cancelled) return;
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not build a plan.");
        navigate("/", { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [pending, navigate]);

  useEffect(() => {
    if (!shared || !shareId) return;
    api
      .get(`/share/${shareId}`)
      .then(({ data }) => setPlan({ ...data, candidates: { dinner: [], drinks: [] } }))
      .catch(() => setError("This shared plan doesn't exist or has expired."));
  }, [shared, shareId]);

  return { plan, setPlan, pending, shareId, error };
}
